import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckboxGroup, TextField } from '@fly/core';
import { FlySvgSprite } from '@fly/svg';

export interface FlyCheckboxDropdownOption {
  value: string;
  label: string;
  icon?: string;
}

export interface FlyCheckboxDropdownProps {
  options: FlyCheckboxDropdownOption[];
  value: string[];
  onValueChange: (values: string[]) => void;
  loading?: boolean;
  searchThreshold?: number;
  'data-testid'?: string;
}

const DEFAULT_SEARCH_THRESHOLD = 5;

export function FlyCheckboxDropdown({
  options,
  value,
  onValueChange,
  loading = false,
  searchThreshold = DEFAULT_SEARCH_THRESHOLD,
  'data-testid': testId,
}: FlyCheckboxDropdownProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const showSearch = options.length > searchThreshold;

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(lower));
  }, [options, search]);

  if (loading) {
    return (
      <div className='py-fly-3 text-size-fly-2 text-(--fly-color-scheme-gray-11)'>
        {t('app.common.loading', { defaultValue: 'Loading...' })}
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      {showSearch && (
        <TextField
          size='1'
          className='mb-fly-3'
          placeholder={t('app.common.search', { defaultValue: 'Search...' })}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(e.target.value)
          }
        />
      )}
      {filteredOptions.length === 0 ? (
        <div className='py-fly-3 text-size-fly-2 text-(--fly-color-scheme-gray-11)'>
          {t('app.common.no_results', { defaultValue: 'No results found' })}
        </div>
      ) : (
        <div
          className='max-h-[200px] overflow-y-auto'
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--gray-a8) transparent',
          }}
        >
          {/* @ts-expect-error Radix CheckboxGroup.Root types don't accept children directly — fixed in radix-ui/themes#1234 */}
          <CheckboxGroup.Root value={value} onValueChange={onValueChange}>
            {filteredOptions.map((opt, index, array) => (
              <CheckboxGroup.Item
                key={opt.value}
                value={opt.value}
                data-testid={
                  testId ? `${testId}-checkbox-${opt.value}` : undefined
                }
                className={`w-full min-w-[100px] py-fly-3 text-size-fly-2 text-custom-12 ${
                  index !== array.length - 1
                    ? 'border-b-[0.5px] border-(--fly-color-scheme-gray-3)'
                    : ''
                }`}
              >
                <span className='inline-flex items-center gap-fly-2'>
                  {opt.icon && <FlySvgSprite name={opt.icon} size={16} />}
                  {opt.label}
                </span>
              </CheckboxGroup.Item>
            ))}
          </CheckboxGroup.Root>
        </div>
      )}
    </div>
  );
}
